import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { RefreshCwIcon } from 'lucide-react';
import { RollingCounter } from '../ui/RollingCounter';
import { useMotionPreference } from '../../hooks/useMotionPreference';
const BUDGET = 5;
const INITIAL_COST = [0.2, 0.6, 0.9, 1.4, 1.9, 2.3, 2.8];
const euro = (v: number) => `€ ${v.toFixed(2).replace('.', ',')}`;
export function LiveDataDemo() {
  const reduce = useMotionPreference();
  const [waiting, setWaiting] = useState(3);
  const [render, setRender] = useState(35);
  const [cost, setCost] = useState(INITIAL_COST);
  const refresh = () => {
    setWaiting(w => w >= 5 ? 2 : w + 1);
    setRender(r => r >= 100 ? 20 : Math.min(100, r + 20));
    setCost(c => {
      const lastVal = c[c.length - 1];
      if (lastVal >= BUDGET * 0.95) return INITIAL_COST;
      return [...c, Math.min(BUDGET, lastVal + 0.3 + Math.random() * 0.4)].slice(-10);
    });
  };
  const current = cost[cost.length - 1];
  const warn = current >= BUDGET * 0.8;
  const W = 300;
  const H = 100;
  const pts = cost.map((v, i) => [i / (cost.length - 1) * W, H - v / BUDGET * (H - 8)] as const);
  const d = pts.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const budgetY = H - (H - 8);
  return <div className="space-y-8" data-id="element-897" data-mp="mhjgnu1hicvt">
      <div className="flex items-end justify-between gap-4" data-id="element-898" data-mp="mibb31jci4ki">
        <div data-id="element-899" data-mp="1k5a3h0308qcl">
          <p className="text-[14px] text-ink-muted" data-id="element-900" data-mp="1k51s5cmj5t3i">Wacht op jou</p>
          <RollingCounter value={waiting} className="font-display text-[56px] font-light leading-none text-ink" data-id="element-901" data-mp="1f5p1egsjg1vn" />
        </div>
        <button type="button" onClick={refresh} className="inline-flex h-10 items-center gap-2 whitespace-nowrap rounded-full border border-white/10 px-4 text-[14px] font-medium text-ink-soft transition-colors duration-150 hover:border-white/20 hover:text-ink" data-id="element-902" data-mp="1ae70ttufg3b7">
          <RefreshCwIcon className="h-4 w-4" aria-hidden data-id="element-903" data-mp="1y49ugedzphbx" /> Nieuwe data
        </button>
      </div>

      <div data-id="element-904" data-mp="3sc5adajy2741">
        <div className="flex items-baseline justify-between text-[14px]" data-id="element-905" data-mp="3sdop2wpyca3p">
          <span className="text-ink-soft" data-id="element-906" data-mp="94eaukqhk7b">Studio · beeld landingspagina</span>
          <span className="tabular-nums text-ink-muted" data-id="element-907" data-mp="3imln7sp77r3d">{render >= 100 ? 'Klaar' : `${render}%`}</span>
        </div>
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/[0.07]" data-id="element-908" data-mp="3nl59ju7pf4w9">
          <motion.div className="h-full rounded-full" style={{
          backgroundColor: '#FF9C8A'
        }} initial={false} animate={{
          width: `${render}%`
        }} transition={{
          duration: reduce ? 0 : 0.3,
          ease: 'linear'
        }} data-id="element-909" data-mp="38oec3qhlc39a" />
        </div>
      </div>

      <div data-id="element-910" data-mp="34msx9zlhxewg">
        <div className="flex items-baseline justify-between text-[14px]" data-id="element-911" data-mp="34mauek4bw2fk">
          <span className="text-ink-soft" data-id="element-912" data-mp="34onkjkk8pj3p">Cloudkosten vandaag</span>
          <span className={`tabular-nums ${warn ? 'text-amber' : 'text-ink-muted'}`} data-id="element-913" data-mp="xwwd1szfwth0">
            {euro(current)} van {euro(BUDGET)}
          </span>
        </div>
        <svg viewBox={`0 0 ${W} ${H}`} className="mt-3 h-28 w-full overflow-visible" role="img" aria-label={`Cloudkosten ${euro(current)} van ${euro(BUDGET)}`} data-id="element-914" data-mp="1cyl9iiif66jy">
          <line x1="0" x2={W} y1={budgetY} y2={budgetY} stroke="rgba(255,255,255,0.18)" strokeDasharray="4 4" data-id="element-915" data-mp="17ygrgo1v53rm" />
          <line x1="0" x2={W} y1={H - 0.8 * (H - 8)} y2={H - 0.8 * (H - 8)} stroke="rgba(242,166,90,0.25)" strokeDasharray="2 4" data-id="element-916" data-mp="1h0zogriywm5y" />
          <motion.path key={cost.length + current} d={d} fill="none" stroke={warn ? '#F2A65A' : 'rgb(var(--accent))'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" initial={reduce ? {
          opacity: 0
        } : {
          pathLength: 0
        }} animate={reduce ? {
          opacity: 1
        } : {
          pathLength: 1
        }} transition={{
          duration: 0.3,
          ease: [0.23, 1, 0.32, 1]
        }} data-id="element-917" data-mp="1vot76l3co4sr" />
        </svg>
        <p className={`mt-2 text-[14px] ${warn ? 'text-amber' : 'text-ink-muted'}`} data-id="element-918" data-mp="6bpq4bowe9tn">
          {warn ? 'Boven 80%: Anna schakelt voor de rest van de dag over op lokale modellen.' : 'Stippellijn: budget. Bij 80% wordt de lijn amber.'}
        </p>
      </div>
    </div>;
}